// Per-sector override editor for otherPaymentTypes[]. The engine reads
// sectorOverrides; this component is the UI that lets users see/edit
// the per-sector map.
//
// Shape: otherPaymentType.sectorOverrides = {
//   [sectorCode]: { amount, nonTaxableLimit }
// }

import { useTranslation } from 'react-i18next';
import {
  Card,
  Collapse,
  Table,
  Button,
  Input,
  InputNumber,
  Popconfirm,
  Tag,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';

function SectorOverridesEditor({ overrides, onChange }) {
  const { t } = useTranslation(['forms']);
  const rows = Object.entries(overrides || {}).map(([sectorCode, v]) => ({
    sectorCode,
    amount: v?.amount ?? null,
    nonTaxableLimit: v?.nonTaxableLimit ?? null,
  }));

  const updateRow = (sectorCode, patch) => {
    const next = { ...(overrides || {}) };
    next[sectorCode] = { ...(next[sectorCode] || {}), ...patch };
    onChange(next);
  };
  const renameRow = (oldCode, newCode) => {
    if (!newCode || oldCode === newCode) return;
    const next = { ...(overrides || {}) };
    next[newCode] = next[oldCode];
    delete next[oldCode];
    onChange(next);
  };
  const addRow = () => {
    const next = { ...(overrides || {}) };
    let i = 1;
    while (next[`sector${i}`]) i += 1;
    next[`sector${i}`] = { amount: null, nonTaxableLimit: null };
    onChange(next);
  };
  const removeRow = (sectorCode) => {
    const next = { ...(overrides || {}) };
    delete next[sectorCode];
    onChange(next);
  };

  return (
    <div className="pl-4">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs text-gray-500">
          {t('ozSectorOverrideHint')}
        </span>
        <Button size="small" icon={<PlusOutlined />} onClick={addRow}>
          {t('ozAddSector')}
        </Button>
      </div>
      <Table
        size="small"
        rowKey="sectorCode"
        dataSource={rows}
        pagination={false}
        columns={[
          {
            title: t('ozSectorCode'),
            dataIndex: 'sectorCode',
            width: 140,
            render: (v) => (
              <Input
                size="small"
                defaultValue={v}
                onBlur={(e) => renameRow(v, e.target.value)}
              />
            ),
          },
          {
            title: t('ozActiveDeductionAmount'),
            dataIndex: 'amount',
            width: 120,
            render: (v, r) => (
              <InputNumber
                size="small"
                value={v}
                onChange={(val) => updateRow(r.sectorCode, { amount: val })}
                style={{ width: 110 }}
              />
            ),
          },
          {
            title: t('ozNonTaxableLimit'),
            dataIndex: 'nonTaxableLimit',
            width: 130,
            render: (v, r) => (
              <InputNumber
                size="small"
                value={v}
                onChange={(val) =>
                  updateRow(r.sectorCode, { nonTaxableLimit: val })
                }
                style={{ width: 120 }}
              />
            ),
          },
          {
            title: '',
            key: 'actions',
            width: 40,
            render: (_, r) => (
              <Popconfirm
                title={t('confirmDelete')}
                onConfirm={() => removeRow(r.sectorCode)}
              >
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            ),
          },
        ]}
      />
    </div>
  );
}

export default function OtherPaymentsSectorOverrides({ items, onChange }) {
  const { t } = useTranslation(['forms']);

  if (!items?.length) {
    return (
      <Card size="small" title={t('ozOtherPaymentSectorOverrides')}>
        <div className="text-xs text-gray-400">
          {t('ozOtherPaymentSectorOverridesEmpty')}
        </div>
      </Card>
    );
  }

  return (
    <Card
      size="small"
      title={t('ozOtherPaymentSectorOverrides')}
      className="mb-4"
    >
      <Collapse
        size="small"
        items={items.map((it, idx) => ({
          key: String(idx),
          label: (
            <span>
              <code className="mr-2">{it.code}</code>
              {it.name}{' '}
              <Tag color="blue">
                {t('ozSectorCount', {
                  count: Object.keys(it.sectorOverrides || {}).length,
                })}
              </Tag>
            </span>
          ),
          children: (
            <SectorOverridesEditor
              overrides={it.sectorOverrides}
              onChange={(next) => {
                const copy = [...items];
                copy[idx] = { ...copy[idx], sectorOverrides: next };
                onChange(copy);
              }}
            />
          ),
        }))}
      />
    </Card>
  );
}
